/**
 * Persistent VIVA HUB header shown when viewing any hub app
 */

import React from 'react'
import { Link } from 'react-router-dom'
import { useSchedulerAuth } from '../../contexts/SchedulerAuthContext'
import VersionBadge from '../VersionBadge'
import HubAppIcon, { SignOutIcon } from './HubAppIcon'
import { HubHeaderIconButton, HubHeaderIconLink } from './HubHeaderIconButton'
import HubThemeToggle from './HubThemeToggle'
import HubBrandLogo from './HubBrandLogo'

function MenuIcon({ className = 'w-5 h-5' }) {
  return (
    <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
    </svg>
  )
}

export default function HubAppHeader({ appName, withSidebar = false, onOpenSidebar }) {
  const { logout } = useSchedulerAuth()

  return (
    <header className="bg-white">
      <div className={withSidebar ? 'px-4 sm:px-6 lg:px-8' : 'max-w-7xl mx-auto px-4 sm:px-6 lg:px-8'}>
        <div className="flex items-center justify-between h-14 sm:h-16">
          <div className="flex items-center gap-2 sm:gap-3 min-w-0">
            {withSidebar && onOpenSidebar ? (
              <button
                type="button"
                onClick={onOpenSidebar}
                className="inline-flex lg:hidden items-center justify-center w-9 h-9 rounded-md text-gray-500 hover:text-gray-900 hover:bg-gray-100 transition-colors"
                aria-label="Open navigation"
              >
                <MenuIcon />
              </button>
            ) : null}

            {!withSidebar ? (
              <Link to="/hub" className="flex items-center gap-2 shrink-0">
                <HubBrandLogo className="h-8 w-8 sm:h-9 sm:w-9" />
                <span className="text-xl sm:text-2xl font-bold text-gray-900">VIVA HUB</span>
              </Link>
            ) : (
              <Link
                to="/hub"
                className="lg:hidden flex items-center gap-2 shrink-0"
              >
                <HubBrandLogo className="h-8 w-8" />
                <span className="text-xl font-bold text-gray-900">VIVA HUB</span>
              </Link>
            )}

            {appName ? (
              <nav
                aria-label="Breadcrumb"
                className={[
                  'items-center gap-2 min-w-0 text-base font-semibold',
                  withSidebar ? 'hidden sm:flex' : 'hidden sm:flex',
                ].join(' ')}
              >
                {!withSidebar ? (
                  <span className="text-gray-300" aria-hidden="true">/</span>
                ) : null}
                <Link to="/hub" className="text-gray-500 hover:text-green-700 transition-colors">
                  Apps
                </Link>
                <span className="text-gray-300" aria-hidden="true">/</span>
                <span className="font-semibold text-gray-900 truncate">{appName}</span>
              </nav>
            ) : withSidebar ? (
              <h1 className="hidden lg:block text-xl font-bold text-gray-900 truncate">
                Home
              </h1>
            ) : null}
          </div>

          <div className="flex items-center gap-1 sm:gap-2 shrink-0">
            <HubThemeToggle />

            <HubHeaderIconLink to="/hub/profile" label="Profile">
              <HubAppIcon appId="profile" className="w-5 h-5" />
            </HubHeaderIconLink>

            <HubHeaderIconButton onClick={logout} label="Sign out">
              <SignOutIcon className="w-5 h-5" />
            </HubHeaderIconButton>

            {!withSidebar && !appName ? (
              <VersionBadge className="sm:hidden text-[10px] text-gray-400 tabular-nums" />
            ) : null}

            {!withSidebar ? (
              <div className="hidden sm:flex items-center border-l border-gray-200 pl-2 ml-1">
                <VersionBadge className="text-[10px] text-gray-400 tabular-nums" />
              </div>
            ) : null}
          </div>
        </div>

        {appName && !withSidebar ? (
          <div className="sm:hidden pb-3 -mt-1 flex items-center justify-between gap-2">
            <nav aria-label="Breadcrumb" className="flex min-w-0 items-center gap-2 text-base font-semibold">
              <Link to="/hub" className="text-gray-500 hover:text-green-700">
                Apps
              </Link>
              <span className="text-gray-300" aria-hidden="true">/</span>
              <span className="truncate font-semibold text-gray-900">{appName}</span>
            </nav>
            <VersionBadge className="text-[10px] text-gray-400 tabular-nums shrink-0" />
          </div>
        ) : null}

        {appName && withSidebar ? (
          <div className="sm:hidden pb-3 -mt-1">
            <p className="truncate text-base font-semibold text-gray-900">{appName}</p>
          </div>
        ) : null}
      </div>
    </header>
  )
}
